import { supabase, getCurrentUserId, isSupabaseConfigured } from "@/lib/supabase";
import type { OrganizationPolicy } from "@/types/workflowEngine";

const DEFAULT_POLICY: OrganizationPolicy = {
  id: "default",
  userId: null,
  workspaceId: null,
  scope: "workspace",
  allowedModels: [],
  security: {},
  piiRules: { redactInLogs: false },
  retention: { runEventDays: 90 },
  costLimits: { maxCreditsPerRun: 50 },
  approvalThreshold: { sideEffectRequiresReview: true, minConfidence: 0.5 },
};

function mapRow(row: Record<string, unknown>): OrganizationPolicy {
  return {
    id: row.id as string,
    userId: (row.user_id as string) ?? null,
    workspaceId: (row.workspace_id as string) ?? null,
    scope: row.scope as OrganizationPolicy["scope"],
    allowedModels: (row.allowed_models as string[]) ?? [],
    security: (row.security as Record<string, unknown>) ?? {},
    piiRules: (row.pii_rules as Record<string, unknown>) ?? {},
    retention: (row.retention as Record<string, unknown>) ?? {},
    costLimits: (row.cost_limits as Record<string, unknown>) ?? {},
    approvalThreshold: (row.approval_threshold as Record<string, unknown>) ?? {},
  };
}

class OrganizationPoliciesService {
  async getEffectivePolicy(workspaceId?: string | null): Promise<OrganizationPolicy> {
    if (!isSupabaseConfigured) return DEFAULT_POLICY;
    const userId = await getCurrentUserId();
    if (!userId) return DEFAULT_POLICY;

    let query = supabase.from("organization_policies").select("*").eq("user_id", userId);
    if (workspaceId) {
      query = query.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);
    }
    const { data } = await query.order("created_at", { ascending: false }).limit(1).maybeSingle();
    if (!data) return DEFAULT_POLICY;
    return mapRow(data as Record<string, unknown>);
  }

  async upsert(policy: Partial<OrganizationPolicy> & { workspaceId?: string | null }): Promise<OrganizationPolicy | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;
    const { data, error } = await supabase
      .from("organization_policies")
      .insert({
        user_id: userId,
        workspace_id: policy.workspaceId ?? null,
        scope: policy.scope ?? "workspace",
        allowed_models: policy.allowedModels ?? [],
        security: policy.security ?? {},
        pii_rules: policy.piiRules ?? {},
        retention: policy.retention ?? {},
        cost_limits: policy.costLimits ?? {},
        approval_threshold: policy.approvalThreshold ?? {},
      })
      .select()
      .single();
    if (error || !data) return null;
    return mapRow(data as Record<string, unknown>);
  }

  mergePolicies(...layers: Partial<OrganizationPolicy>[]): OrganizationPolicy {
    return layers.reduce(
      (acc, layer) => ({
        ...acc,
        ...layer,
        allowedModels: layer.allowedModels?.length ? layer.allowedModels : acc.allowedModels,
        security: { ...acc.security, ...layer.security },
        piiRules: { ...acc.piiRules, ...layer.piiRules },
        retention: { ...acc.retention, ...layer.retention },
        costLimits: { ...acc.costLimits, ...layer.costLimits },
        approvalThreshold: { ...acc.approvalThreshold, ...layer.approvalThreshold },
      }),
      { ...DEFAULT_POLICY }
    );
  }
}

export const organizationPoliciesService = new OrganizationPoliciesService();
